// src/pages/admin/GlobalSettings.tsx (renamed to SiteSettings.tsx or keep as GlobalSettings but focus on Site Settings)
import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  useMediaQuery,
  useTheme,
  Drawer,
  CircularProgress,
  Alert,
  Snackbar,
  Button,
  TextField,
  Card,
  Stack,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Switch,
  FormControlLabel,
} from '@mui/material';
import { 
  Save, 
  Refresh,
  Settings as SettingsIcon,
  Language,
  Add,
  Close,
  Edit,
  ExpandMore,
} from '@mui/icons-material';

import Slidemenu from '../../components/admin/adminSlidemenu.tsx';
import AdminHeader from '../../components/admin/adminHeader.tsx';

import {
  useListSiteSettingsQuery,
  useSaveSiteSettingMutation,
} from '../../features/admin/globalSettingsApi.ts';

const GlobalSettings = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [mobileOpen, setMobileOpen] = useState(false);
  
  // Site settings states
  const [siteSettingDialog, setSiteSettingDialog] = useState(false);
  const [editingSetting, setEditingSetting] = useState<{ key_name: string; value: string } | null>(null);
  const [siteKeyName, setSiteKeyName] = useState('');
  const [siteValue, setSiteValue] = useState('');
  const [isNewSetting, setIsNewSetting] = useState(false);
  
  const [snackbar, setSnackbar] = useState({ 
    open: false, 
    message: '', 
    severity: 'success' as 'success' | 'error' 
  });

  // Site settings hooks
  const {
    data: siteSettingsData,
    isLoading: isSiteSettingsLoading,
    isError: isSiteSettingsError,
    refetch: refetchSiteSettings,
  } = useListSiteSettingsQuery(undefined, {
    refetchOnMountOrArgChange: true,
    refetchOnFocus: true,
  });

  const [saveSiteSetting, { isLoading: isSiteSettingSaving }] = useSaveSiteSettingMutation();

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  // Site settings handlers
  const handleOpenEditDialog = (setting: { key_name: string; value: string }) => {
    setEditingSetting(setting);
    setSiteKeyName(setting.key_name);
    setSiteValue(setting.value);
    setIsNewSetting(false);
    setSiteSettingDialog(true);
  };

  const handleOpenAddDialog = () => {
    setEditingSetting(null);
    setSiteKeyName('');
    setSiteValue('');
    setIsNewSetting(true);
    setSiteSettingDialog(true);
  };

  const handleCloseSiteDialog = () => {
    setSiteSettingDialog(false);
    setEditingSetting(null);
    setSiteKeyName('');
    setSiteValue('');
  };

  const handleSaveSiteSetting = async () => {
    if (!siteKeyName.trim()) {
      setSnackbar({ open: true, message: 'Please enter a key name', severity: 'error' });
      return;
    }
    if (!siteValue.trim()) {
      setSnackbar({ open: true, message: 'Please enter a value', severity: 'error' });
      return;
    }

    try {
      const result = await saveSiteSetting({ key_name: siteKeyName, value: siteValue }).unwrap();
      if (result.success) {
        setSnackbar({ open: true, message: result.message || 'Setting saved successfully!', severity: 'success' });
        handleCloseSiteDialog();
        await refetchSiteSettings();
      } else {
        throw new Error(result.message || 'Failed to save setting');
      }
    } catch (err: any) {
      setSnackbar({ open: true, message: err?.message || 'Error saving site setting.', severity: 'error' });
    }
  };

  const handleRefresh = async () => {
    try {
      await refetchSiteSettings();
      setSnackbar({ open: true, message: 'Site settings refreshed successfully', severity: 'success' });
    } catch (error) {
      setSnackbar({ open: true, message: 'Failed to refresh site settings', severity: 'error' });
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  // Feature controls required by the storefront.
  const FEATURE_CONTROLS = [
    { key: 'membership_plans_enabled', label: 'Membership Plans', description: 'Show Membership Plans in the website header and membership pages.', defaultValue: '0' },
    { key: 'support_callback_enabled', label: 'Customer Support - Callback', description: 'Allow Callback support for entry-level customers.', defaultValue: '1' },
    { key: 'support_email_enabled', label: 'Customer Support - Email', description: 'Allow Email support for entry-level customers.', defaultValue: '1' },
    { key: 'support_live_chat_enabled', label: 'Customer Support - Live Chat', description: 'Show Live Chat support.', defaultValue: '0' },
    { key: 'support_toll_free_enabled', label: 'Customer Support - Toll-Free', description: 'Show Toll-Free support.', defaultValue: '0' },
    { key: 'support_whatsapp_enabled', label: 'Customer Support - WhatsApp', description: 'Show WhatsApp support.', defaultValue: '0' },
  ];

  const siteSettingsList = siteSettingsData?.data || [];
  const getFeatureValue = (key: string, defaultValue: string) =>
    siteSettingsList.find(s => s.key_name === key)?.value ?? defaultValue;

  // customer website address (referral links, product share links in the
  // app and emails). Kept here so going live needs no new app build.
  const savedPortalUrl = getFeatureValue('portal_url', '');
  const [portalUrl, setPortalUrl] = useState('');
  const [portalTouched, setPortalTouched] = useState(false);
  useEffect(() => {
    if (!portalTouched) setPortalUrl(savedPortalUrl);
  }, [savedPortalUrl, portalTouched]);
  const portalValid = /^https?:\/\/[^\s/]+\.[^\s]+$/i.test(portalUrl.trim());
  const savePortalUrl = async () => {
    const value = portalUrl.trim().replace(/\/+$/, '');
    if (!/^https?:\/\/[^\s/]+\.[^\s]+$/i.test(value)) {
      setSnackbar({ open: true, message: 'Enter the full website address, e.g. https://www.voto.ae', severity: 'error' });
      return;
    }
    try {
      const result: any = await saveSiteSetting({ key_name: 'portal_url', value }).unwrap();
      if (result?.error === true) throw new Error(result?.message);
      setPortalTouched(false);
      setSnackbar({ open: true, message: 'Website address saved.', severity: 'success' });
      await refetchSiteSettings();
    } catch (err: any) {
      setSnackbar({ open: true, message: err?.message || 'Failed to save the website address.', severity: 'error' });
    }
  };

  // return period (days): website + app texts and the referral reward
  // waiting time all use this one value
  const savedReturnDays = getFeatureValue('return_period_days', '30');
  const [returnDays, setReturnDays] = useState('');
  const [returnTouched, setReturnTouched] = useState(false);
  useEffect(() => {
    if (!returnTouched) setReturnDays(savedReturnDays);
  }, [savedReturnDays, returnTouched]);
  const returnDaysValid = /^\d{1,3}$/.test(returnDays.trim());
  const saveReturnDays = async () => {
    if (!returnDaysValid) {
      setSnackbar({ open: true, message: 'Enter the number of days (0 - 999).', severity: 'error' });
      return;
    }
    try {
      const result: any = await saveSiteSetting({ key_name: 'return_period_days', value: String(Number(returnDays)) }).unwrap();
      if (result?.error === true) throw new Error(result?.message);
      setReturnTouched(false);
      setSnackbar({ open: true, message: 'Return period saved.', severity: 'success' });
      await refetchSiteSettings();
    } catch (err: any) {
      setSnackbar({ open: true, message: err?.message || 'Failed to save the return period.', severity: 'error' });
    }
  };

  const handleFeatureToggle = async (key: string, enabled: boolean) => {
    try {
      const result = await saveSiteSetting({ key_name: key, value: enabled ? '1' : '0' }).unwrap();
      if (!result.success) throw new Error(result.message);
      setSnackbar({ open: true, message: `${key.replaceAll('_', ' ')} ${enabled ? 'enabled' : 'disabled'}.`, severity: 'success' });
      await refetchSiteSettings();
    } catch (err: any) {
      setSnackbar({ open: true, message: err?.message || 'Failed to update setting.', severity: 'error' });
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh', bgcolor: '#f1f2f4' }}>
      <Box sx={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
        <Box sx={{ width: '250px', flexShrink: 0, display: { xs: 'none', md: 'block' } }}>
          <Slidemenu mobileOpen={false} onToggle={handleDrawerToggle} />
        </Box>
        
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': { width: 250 },
          }}
        >
          <Slidemenu onToggle={handleDrawerToggle} mobileOpen={false} />
        </Drawer>
        
        <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          <AdminHeader onToggleMenu={handleDrawerToggle} />
          <Box sx={{ flexGrow: 1, overflow: 'auto', backgroundColor: '#f1f2f4' }}>
            <Box sx={{ maxWidth: 1200, mx: 'auto', p: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <SettingsIcon sx={{ color: '#202223', fontSize: 28 }} />
                  <Typography variant="h5" sx={{ fontWeight: 600, color: '#202223' }}>
                    Site Settings
                  </Typography>
                </Box>
                
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button
                    variant="outlined"
                    startIcon={<Refresh />}
                    onClick={handleRefresh}
                    disabled={isSiteSettingsLoading}
                    sx={{
                      textTransform: 'none',
                      borderColor: '#c9cccf',
                      color: '#202223',
                      borderRadius: '8px',
                      fontWeight: 500,
                      '&:hover': { borderColor: '#202223', bgcolor: 'transparent' }
                    }}
                  >
                    Refresh
                  </Button>
                  <Button
                    variant="contained"
                    startIcon={<Add />}
                    onClick={handleOpenAddDialog}
                    sx={{
                      bgcolor: '#008060', textTransform: 'none', fontWeight: 600, borderRadius: '8px',
                      '&:hover': { bgcolor: '#006e52' }
                    }}
                  >
                    Add Setting
                  </Button>
                </Box>
              </Box>

              <Card sx={{ mb: 2, borderRadius: '16px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)', border: '1px solid #e1e3e5', overflow: 'hidden' }}>
                <Box sx={{ p: 3, bgcolor: 'white' }}>
                  <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>Customer Website Address</Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Used for referral links and shared product links (app, website, emails). Change it here when moving to live - no new app build needed.
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start', flexWrap: 'wrap' }}>
                    <TextField
                      size="small"
                      label="Website address"
                      placeholder="https://fftestportal.sarsetech.com"
                      value={portalUrl}
                      onChange={e => { setPortalUrl(e.target.value); setPortalTouched(true); }}
                      error={!!portalUrl && !portalValid}
                      helperText={!!portalUrl && !portalValid ? 'Full address starting with https://' : savedPortalUrl ? `Saved: ${savedPortalUrl}` : 'Not set - the server default is used'}
                      sx={{ flex: 1, minWidth: 280 }}
                    />
                    <Button
                      variant="contained"
                      disableElevation
                      onClick={savePortalUrl}
                      disabled={!portalTouched || !portalValid}
                      sx={{ bgcolor: '#008060', textTransform: 'none', fontWeight: 600, borderRadius: '8px', height: 40, '&:hover': { bgcolor: '#006e52' } }}
                    >
                      Save
                    </Button>
                  </Box>
                </Box>
              </Card>

              <Card sx={{ mb: 2, borderRadius: '16px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)', border: '1px solid #e1e3e5', overflow: 'hidden' }}>
                <Box sx={{ p: 3, bgcolor: 'white' }}>
                  <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>Return Period</Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Days a customer can return an item after delivery. Shown on the website and the app ("30-day return policy", "Free returns within 30 days"),
                    and referral reward points are given only after this period.
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start', flexWrap: 'wrap' }}>
                    <TextField
                      size="small"
                      label="Return period"
                      type="number"
                      value={returnDays}
                      onChange={e => { setReturnDays(e.target.value); setReturnTouched(true); }}
                      error={!!returnDays && !returnDaysValid}
                      helperText={`Saved: ${savedReturnDays} days`}
                      InputProps={{ endAdornment: <InputAdornment position="end">days</InputAdornment> }}
                      sx={{ width: 220 }}
                    />
                    <Button
                      variant="contained"
                      disableElevation
                      onClick={saveReturnDays}
                      disabled={!returnTouched || !returnDaysValid}
                      sx={{ bgcolor: '#008060', textTransform: 'none', fontWeight: 600, borderRadius: '8px', height: 40, '&:hover': { bgcolor: '#006e52' } }}
                    >
                      Save
                    </Button>
                  </Box>
                </Box>
              </Card>

              <Card sx={{ mb: 2, borderRadius: '16px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)', border: '1px solid #e1e3e5', overflow: 'hidden' }}>
                <Box sx={{ p: 3, bgcolor: 'white' }}>
                  <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>Website Feature Controls</Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Control which membership and customer-support options are visible on the website.
                  </Typography>
                  <Stack spacing={1}>
                    {FEATURE_CONTROLS.map(control => {
                      const enabled = getFeatureValue(control.key, control.defaultValue) === '1';
                      return (
                        <Box key={control.key} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 1.5, border: '1px solid #e1e3e5', borderRadius: 2 }}>
                          <Box sx={{ pr: 2 }}>
                            <Typography variant="body1" fontWeight={600}>{control.label}</Typography>
                            <Typography variant="caption" color="text.secondary">{control.description}</Typography>
                          </Box>
                          <FormControlLabel
                            sx={{ mr: 0 }}
                            label={enabled ? 'Enabled' : 'Disabled'}
                            control={
                              <Switch
                                checked={enabled}
                                onChange={e => handleFeatureToggle(control.key, e.target.checked)}
                                color="success"
                              />
                            }
                          />
                        </Box>
                      );
                    })}
                  </Stack>
                </Box>
              </Card>

              <Card sx={{ borderRadius: '16px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)', border: '1px solid #e1e3e5', overflow: 'hidden' }}>
                {isSiteSettingsLoading && (
                  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400, bgcolor: 'white' }}>
                    <CircularProgress size={32} sx={{ color: '#008060' }} />
                  </Box>
                )}

                {isSiteSettingsError && !isSiteSettingsLoading && (
                  <Box sx={{ p: 3, bgcolor: 'white' }}>
                    <Alert severity="error">Failed to load site settings. Please try again.</Alert>
                  </Box>
                )}

                {!isSiteSettingsLoading && !isSiteSettingsError && (
                  <Box sx={{ p: 3, bgcolor: 'white' }}>
                    <Accordion 
                      defaultExpanded 
                      sx={{ 
                        boxShadow: 'none', 
                        border: '1px solid #e1e3e5',
                        '&:before': { display: 'none' },
                        borderRadius: '12px !important',
                        overflow: 'hidden'
                      }}
                    >
                      <AccordionSummary
                        expandIcon={<ExpandMore />}
                        sx={{
                          bgcolor: '#f6f6f7',
                          borderBottom: '1px solid #e1e3e5',
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Language sx={{ color: '#008060', fontSize: 24 }} />
                          <Typography variant="h6" sx={{ fontWeight: 600, color: '#202223' }}>
                            Site Configuration
                          </Typography>
                        </Box>
                      </AccordionSummary>
                      <AccordionDetails sx={{ p: 3 }}>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                          Manage key-value site configuration settings.
                        </Typography>

                        {siteSettingsList.length === 0 ? (
                          <Box sx={{ textAlign: 'center', py: 8, bgcolor: '#fafafa', borderRadius: '8px', border: '1px dashed #e1e3e5' }}>
                            <Language sx={{ fontSize: 48, color: '#bdbdbd', mb: 2 }} />
                            <Typography variant="h6" color="text.secondary" gutterBottom>No Site Settings</Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                              Add your first site setting to get started.
                            </Typography>
                            <Button
                              variant="outlined"
                              startIcon={<Add />}
                              onClick={handleOpenAddDialog}
                              sx={{ textTransform: 'none', borderColor: '#008060', color: '#008060', borderRadius: '8px' }}
                            >
                              Add Setting
                            </Button>
                          </Box>
                        ) : (
                          <TableContainer component={Paper} sx={{ boxShadow: 'none', border: '1px solid #e1e3e5' }}>
                            <Table>
                              <TableHead sx={{ bgcolor: '#f6f6f7' }}>
                                <TableRow>
                                  <TableCell sx={{ fontWeight: 600 }}>ID</TableCell>
                                  <TableCell sx={{ fontWeight: 600 }}>Key Name</TableCell>
                                  <TableCell sx={{ fontWeight: 600 }}>Value</TableCell>
                                  <TableCell sx={{ fontWeight: 600 }}>Actions</TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {siteSettingsList.map((setting) => (
                                  <TableRow key={setting.id} hover>
                                    <TableCell>{setting.id}</TableCell>
                                    <TableCell>
                                      <Chip
                                        label={setting.key_name}
                                        size="small"
                                        sx={{ bgcolor: '#f3e5f5', color: '#6a1b9a', fontWeight: 500, fontFamily: 'monospace' }}
                                      />
                                    </TableCell>
                                    <TableCell>
                                      <Typography variant="body2">{setting.value}</Typography>
                                    </TableCell>
                                    <TableCell>
                                      <Tooltip title="Edit Setting">
                                        <IconButton
                                          size="small"
                                          color="primary"
                                          onClick={() => handleOpenEditDialog(setting)}
                                        >
                                          <Edit fontSize="small" />
                                        </IconButton>
                                      </Tooltip>
                                    </TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </TableContainer>
                        )}
                      </AccordionDetails>
                    </Accordion>
                  </Box>
                )}
              </Card>
            </Box>
          </Box>
        </Box>
      </Box>

      {/* ── Site Setting Dialog ── */}
      <Dialog open={siteSettingDialog} onClose={handleCloseSiteDialog} maxWidth="sm" fullWidth
        PaperProps={{ sx: { borderRadius: '16px' } }}
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
          <Typography variant="h6" fontWeight={600}>
            {isNewSetting ? 'Add Site Setting' : 'Edit Site Setting'}
          </Typography>
          <IconButton onClick={handleCloseSiteDialog} size="small">
            <Close />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ pt: '20px !important', pb: 2 }}>
          <Stack spacing={3}>
            <TextField
              label="Key Name"
              value={siteKeyName}
              onChange={(e) => setSiteKeyName(e.target.value)}
              fullWidth
              placeholder="e.g., shipping_from"
              helperText={
                isNewSetting
                  ? 'Unique identifier for this setting (use underscores, no spaces)'
                  : 'Key name cannot be changed after creation'
              }
              disabled={!isNewSetting}
              InputLabelProps={{ shrink: true }}
              InputProps={
                !isNewSetting
                  ? {
                      endAdornment: (
                        <InputAdornment position="end">
                          <Tooltip title="Key name is locked after creation">
                            <span style={{ display: 'flex', alignItems: 'center', color: '#8c9196', fontSize: 18 }}>
                              🔒
                            </span>
                          </Tooltip>
                        </InputAdornment>
                      ),
                    }
                  : undefined
              }
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '8px',
                  fontFamily: 'monospace',
                  bgcolor: !isNewSetting ? '#f6f6f7' : 'transparent',
                },
                '& .MuiInputLabel-root': { color: '#6d7175' },
                '& .MuiInputLabel-root.Mui-disabled': { color: '#6d7175' },
                '& .MuiOutlinedInput-root.Mui-disabled .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#e1e3e5',
                  borderStyle: 'dashed',
                },
                '& .MuiInputBase-input.Mui-disabled': {
                  WebkitTextFillColor: '#8c9196',
                  fontFamily: 'monospace',
                },
                '& .MuiFormHelperText-root': {
                  color: !isNewSetting ? '#f59e0b' : undefined,
                },
              }}
            />
            <TextField
              label="Value"
              value={siteValue}
              onChange={(e) => setSiteValue(e.target.value)}
              fullWidth
              multiline
              minRows={2}
              placeholder="e.g., Ships from Voto.com"
              helperText="The value for this setting"
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}
            />
          </Stack>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 3, gap: 1 }}>
          <Button
            onClick={handleCloseSiteDialog}
            sx={{ textTransform: 'none', color: '#eeeeee', bgcolor: '#f44336', borderRadius: '8px' }}
          >
            Close
          </Button>
          <Button
            onClick={handleCloseSiteDialog}
            sx={{ textTransform: 'none', color: '#eeeeee', bgcolor: '#F78E12', borderRadius: '8px' }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            startIcon={isSiteSettingSaving ? <CircularProgress size={16} color="inherit" /> : <Save />}
            onClick={handleSaveSiteSetting}
            disabled={isSiteSettingSaving || !siteKeyName.trim() || !siteValue.trim()}
            sx={{
              bgcolor: '#008060', textTransform: 'none', fontWeight: 600, borderRadius: '8px', px: 3,
              '&:hover': { bgcolor: '#006e52' },
              '&.Mui-disabled': { bgcolor: '#c9cccf', color: '#8c9196' }
            }}
          >
            {isSiteSettingSaving ? 'Saving...' : 'Save Setting'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default GlobalSettings;